/** Skeletons para estados de carga. */
import { clsx } from 'clsx';

type SkeletonProps = {
  className?: string;
};

/** Skeleton base con animación shimmer. */
function SkeletonBase({ className }: SkeletonProps) {
  return (
    <div
      className={clsx(
        'rounded-lg bg-gradient-to-r from-bg-tertiary via-gray-200 to-bg-tertiary',
        'bg-[length:200%_100%] animate-shimmer',
        className,
      )}
    />
  );
}

/** Card skeleton para listas. */
export function CardSkeleton() {
  return (
    <div className="rounded-card bg-white p-4 shadow-card animate-fade-in">
      <div className="flex items-center gap-3">
        {/* Avatar/Icono */}
        <SkeletonBase className="h-12 w-12 rounded-xl flex-shrink-0" />
        
        {/* Contenido */}
        <div className="flex-1 space-y-2">
          <SkeletonBase className="h-4 w-3/4" />
          <SkeletonBase className="h-3 w-1/2" />
        </div>
        
        {/* Badge */}
        <SkeletonBase className="h-6 w-16 rounded-full" />
      </div>
    </div>
  );
}

/** Lista de cards skeleton. */
export function CardListSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <CardSkeleton key={i} />
      ))}
    </div>
  );
}

/** Home cards skeleton. */
export function HomeCardSkeleton() {
  return (
    <div className="rounded-card bg-white p-4 shadow-card animate-fade-in">
      <div className="flex items-center gap-4">
        {/* Icono */}
        <SkeletonBase className="h-12 w-12 rounded-xl" />
        
        {/* Contenido */}
        <div className="flex-1 space-y-2">
          <SkeletonBase className="h-5 w-2/3" />
          <SkeletonBase className="h-4 w-full" />
        </div>
        
        {/* Chevron */}
        <SkeletonBase className="h-6 w-6 rounded-full" />
      </div>
    </div>
  );
}

/** Form skeleton. */
export function FormSkeleton() {
  return (
    <div className="rounded-card bg-white p-5 shadow-card space-y-4 animate-fade-in">
      {/* Campo 1 */}
      <div className="space-y-2">
        <SkeletonBase className="h-4 w-20" />
        <SkeletonBase className="h-12 w-full rounded-xl" />
      </div>
      
      {/* Campo 2 */}
      <div className="space-y-2">
        <SkeletonBase className="h-4 w-24" />
        <SkeletonBase className="h-12 w-full rounded-xl" />
      </div>
      
      {/* Botón */}
      <SkeletonBase className="h-12 w-full rounded-xl mt-6" />
    </div>
  );
}

/** Detalle skeleton. */
export function DetailSkeleton() {
  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="rounded-card bg-white p-4 shadow-card space-y-3">
        <div className="flex items-center justify-between">
          <SkeletonBase className="h-6 w-1/2" />
          <SkeletonBase className="h-6 w-20 rounded-full" />
        </div>
        <SkeletonBase className="h-4 w-3/4" />
      </div>
      
      {/* Contenido */}
      <div className="rounded-card bg-white p-4 shadow-card space-y-3">
        <SkeletonBase className="h-4 w-24" />
        <SkeletonBase className="h-5 w-2/3" />
        <SkeletonBase className="h-4 w-1/2" />
      </div>
      
      {/* Acciones */}
      <div className="grid grid-cols-2 gap-3">
        <SkeletonBase className="h-12 rounded-xl" />
        <SkeletonBase className="h-12 rounded-xl" />
      </div>
    </div>
  );
}

/** Línea de texto skeleton. */
export function TextSkeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div className="space-y-2 animate-fade-in">
      {Array.from({ length: lines }).map((_, i) => (
        <SkeletonBase
          key={i}
          className={clsx(
            'h-4',
            i === lines - 1 ? 'w-3/4' : 'w-full',
          )}
        />
      ))}
    </div>
  );
}
